import {escapeHtml, sanitizeHtml} from '../utils/markupHelpers.js';
import {t} from '../localization/translate.js';

/**
 * @module alert
 * @description Provide methods to display messages.
 * @example Notification.warning('Failed to remove a lesson item from a lesson');
 * // add warning alert to the body element
 *
 * @example Notification.info('Notify a user for info.', document.querySelector('#customContainer'));
 * // add info alert to some custom container
 *
 * @example Notification.success('Notify a user for info.', null, () => {alert('Message from callback');});
 * // add success alert with custom callback
 */

/**
 * Create root container for alert
 * @param {HTMLElement} target
 * @returns {HTMLElement} element that hold all the alerts
 */
const createRootContainer = (target) => {
    if (!target.querySelector(`${target.tagName} > .alertContainer`)) {
        // Alert container is not created

        const gridContent = document.createElement('div');
        gridContent.className = 'alertContent';
        const alertContainer = document.createElement('div');
        alertContainer.className = 'alertContainer';

        // add `page` element when container is created in body
        // this is to make container always at the fixed position
        if (target === document.body) alertContainer.className += ' alertContainer--page';
        // Add inline class to have different behaviour for inline alert
        else alertContainer.className += ' alertContainer--inline';

        alertContainer.append(gridContent);
        target.insertBefore(alertContainer, target.firstChild);

        return gridContent;
    }

    // Otherwise, container already exist
    return target === document.body
        ? target.querySelector(`${target.tagName} > .alertContainer > .alertContent`)
        : target.querySelector(`${target.tagName} > .alertContainer`);
};

/**
 * Generate notification element
 * @param {string} type type of notification
 * @param {string} message
 * @param {string|null} [title] optional title field
 * @param {boolean} [isSafeHtml] the copy is already safe HTML and must be inserted, not escaped again
 * @returns {HTMLElement} notification element
 */
const createNotificationElement = (type, message, title, isSafeHtml = false) => {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    
    // Set appropriate ARIA attributes based on notification type
    if (type === 'error' || type === 'warning') {
        notification.setAttribute('role', 'alert');
    } else {
        notification.setAttribute('role', 'status');
    }

    notification.setAttribute('aria-live', 'polite');
    
    // Unique ID for the notification for better focus management
    const notificationId = `notification-${Date.now()}`;
    notification.id = notificationId;
    
    // Ensure the entire message is read
    notification.setAttribute('aria-atomic', 'true');

    let svgForIcon = ``;
    let iconLabel = '';

    // Why we're using inline SVG instead of sprite? Because if these icons are only limited to this notification script, It does not make sense to load these icons in global sprite file (https://github.com/InteractionDesignFoundation/IxDF-web/pull/25491#discussion_r2050638732)
    switch(type) 
    {
        case 'success':
        iconLabel = t('Success:');
        svgForIcon = `
            <div class='icon' aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path d="M19 7.1875L9.375 16.8125L5 12.4375" stroke="#009112" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
        `;
        break;
        case 'warning':
        iconLabel = t('Warning:');
        svgForIcon = `
            <div class='icon' aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.06409 20.6868H19.4471C21.0291 20.6868 22.0221 18.9768 21.2361 17.6028L14.0501 5.03775C13.2591 3.65475 11.2651 3.65375 10.4731 5.03675L3.27509 17.6018C2.48909 18.9758 3.48109 20.6868 5.06409 20.6868Z" stroke="#E42626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12.2524 13.6645V10.5645" stroke="#E42626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12.2451 16.75H12.2551" stroke="#E42626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
        `;
        break;
        case 'error':
        iconLabel = t('Error:');
        svgForIcon = `
            <div class='icon' aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.06409 20.6868H19.4471C21.0291 20.6868 22.0221 18.9768 21.2361 17.6028L14.0501 5.03775C13.2591 3.65475 11.2651 3.65375 10.4731 5.03675L3.27509 17.6018C2.48909 18.9758 3.48109 20.6868 5.06409 20.6868Z" stroke="#E42626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12.2524 13.6645V10.5645" stroke="#E42626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12.2451 16.75H12.2551" stroke="#E42626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
        `;
        break;
        case 'info':
        iconLabel = t('Information:');
        svgForIcon = `
            <div class='icon' aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="12" cy="7" r="1" fill="#232323"/>
                    <path d="M12.75 10C12.75 9.58579 12.4142 9.25 12 9.25C11.5858 9.25 11.25 9.58579 11.25 10H12.75ZM11.25 17C11.25 17.4142 11.5858 17.75 12 17.75C12.4142 17.75 12.75 17.4142 12.75 17H11.25ZM12 10H11.25V17H12H12.75V10H12ZM12 17H12.75V10H12H11.25V17H12Z" fill="#232323"/>
                </svg>
            </div>
        `;
        break;
    }

    const srTypeAnnouncement = `<span class="sr-only">${escapeHtml(iconLabel)}</span>`;

    /**
     * Server-flashed copy arrives as safe HTML, so inserting it is what makes `<b>` and `<a>` survive.
     * Anything a JS caller passes is plain text and is stripped and escaped here instead.
     * @param {string} value
     * @returns {string}
     */
    const asMarkup = (value) => (isSafeHtml ? value : escapeHtml(sanitizeHtml(value)));

    let notificationContent = '';
    
    if (title) {
        notificationContent = `
            <div class="content">
                <h2 class="title">${asMarkup(title)}</h2>
                <div class="subtitle">${asMarkup(message)}</div>
            </div>
        `;
    } else {
        notificationContent = `
            <div class="content">
                <div class="text">${asMarkup(message)}</div>
            </div>
        `;
    }

    let closeButton = `
        <button 
            type="button" 
            class="closeButton" 
            aria-label="${escapeHtml(t('Close notification'))}" 
            aria-controls="${notificationId}"
            tabindex="0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path d="M6.33325 6L18.3333 18" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M18.3333 6L6.33325 18" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
    `;

    let content = `
        ${svgForIcon}
        ${srTypeAnnouncement}
        ${notificationContent}
        ${closeButton}
    `;

    notification.insertAdjacentHTML('afterbegin', content);

    return notification;
};

/**
 * Adds notification to the alert container
 * If there is an existing node then this pushes the existing node down by creating
 * a temp div and increase its height
 * @param {HTMLElement} alertContainer
 * @param {HTMLElement} notification
 */
function addNotification(alertContainer, notification) {
    if (alertContainer.children.length > 0) {
        // Get height of the newly added notification
        // set it invisible
        notification.style.visibility = 'hidden';
        document.body.append(notification);
        const {height} = notification.getBoundingClientRect();
        notification.remove();
        // set it back to visible
        notification.style.visibility = 'visible';

        // Create filler div that will increase in height
        const fillerDiv = document.createElement('div');
        fillerDiv.className = 'fillerDiv';
        alertContainer.prepend(fillerDiv);

        // Listen to transition end event to remove filler div and append notification
        const transitionEnd = ['webkitTransitionEnd', 'oTransitionEnd', 'mstransitionEnd', 'transitionend'];
        const onTransitionOut = () => {
            transitionEnd.forEach((event) => alertContainer.removeEventListener(event, onTransitionOut));
            fillerDiv.remove();
            alertContainer.prepend(notification);
            
            notification.setAttribute('tabindex', '-1');
            
            if (notification instanceof HTMLElement) {
                notification.focus();
            }
            
            // Remove tabindex after focus to avoid keyboard navigation issues
            setTimeout(() => {
                notification.removeAttribute('tabindex');
                const closeButton = notification.querySelector('.closeButton');
                if (closeButton && closeButton instanceof HTMLElement) {
                    closeButton.focus();
                }
            }, 100);
        };
        transitionEnd.forEach((event) => fillerDiv.addEventListener(event, onTransitionOut));

        // Need to increase height after element successfully added and event listener
        // running, setTimeout helps us achieve that
        setTimeout(() => {
            const notificationPadding = 32;
            fillerDiv.style.height = `${height + notificationPadding}px`;
        }, 0);
    } else {
        // If user prefers reduced motion or there are no existing notifications,
        // We will add notification without animation
        alertContainer.prepend(notification);
        
        // Focus management for accessibility
        const closeButton = notification.querySelector('.closeButton');
        if (closeButton && closeButton instanceof HTMLElement) {
            closeButton.focus();
        }
    }
}

/**
 * Show an alert box with the specified type and message.
 * The alert box will be prepended to the specified $container.
 *
 * @private
 * @param {!string} type The alert box type, success|warning|info|alert|secondary.
 * @param {!(string|MessageWithTitle)} message Text to display
 * @param {HTMLElement} [container] element to hold alerts element
 * @param {((container: HTMLElement) => unknown)|null} [onNotificationDisplayedCallback] Callback calls after prepending
 * @param {boolean} autoClose Notification should auto-close after 10 seconds
 * @returns {boolean | unknown} Value returned by optional callback or boolean true
 */
function show(type, message, container = document.body, onNotificationDisplayedCallback = null, autoClose = false) {
    // Generate alert container if not available
    const alertContainer = createRootContainer(container);

    let messageString = '';
    let titleString = null;
    let isSafeHtml = false;
    
    if (typeof message === 'string') {
        messageString = message;
    } else if (message && typeof message === 'object' && 'message' in message) {
        messageString = message.message;
        titleString = message.title;
        isSafeHtml = message.html === true;
    }

    // build notification
    const notification = createNotificationElement(type, messageString, titleString, isSafeHtml);

    const previouslyFocusedElement = document.activeElement;
    
    addNotification(alertContainer, notification);

    // closing animation end listener
    const animationEndEvents = ['webkitAnimationEnd', 'oAnimationEnd', 'msAnimationEnd', 'animationend'];
    const onNotificationTransitOut = () => {
        // Remove animation end listener
        animationEndEvents.forEach((event) => notification.removeEventListener(event, onNotificationTransitOut));
        // and notification block
        notification.remove();
    
        if (previouslyFocusedElement instanceof HTMLElement) {
            previouslyFocusedElement.focus();
        }
    };

    const onUserCloseNotification = (clickEvent) => {
        // Immediately remove click event listener
        clickEvent.currentTarget.removeEventListener('click', onUserCloseNotification);

        // listen to animation end
        animationEndEvents.forEach((event) => notification.addEventListener(event, onNotificationTransitOut));
        notification.classList.add('closingAnimation');
    };

    /** @type {HTMLButtonElement} */
    const closeButton = notification.querySelector('.closeButton');
    closeButton.addEventListener('click', onUserCloseNotification);
    
    // Add keyboard event listener for Escape key to close notification
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            closeButton.click();
            notification.removeEventListener('keydown', handleKeyDown);
        }
    };
    notification.addEventListener('keydown', handleKeyDown);

    if (autoClose) {
        const autoCloseAnnouncement = document.createElement('div');
        autoCloseAnnouncement.className = 'sr-only';
        autoCloseAnnouncement.setAttribute('aria-live', 'polite');
        autoCloseAnnouncement.textContent = t('This notification will automatically close in few seconds.');
        notification.append(autoCloseAnnouncement);
        
        setTimeout(() => {
            closeButton.click();
        }, 10000);
    }

    if (typeof onNotificationDisplayedCallback === 'function') {
        return onNotificationDisplayedCallback(alertContainer);
    }

    return true;
}

/**
 * @typedef {object} MessageWithTitle
 * @property {string} message undocumented
 * @property {string} title undocumented
 * @property {boolean} [html] the copy is already safe HTML; only the server path sets this
 */

export class Notification {
    /**
     * Show success alert box with the specified type and message.
     * The alert box will be prepended to the specified container.
     *
     * @param {!(string|MessageWithTitle)} message Text to display
     * @param {HTMLElement} [container] If not provided - use document.body
     * @param {((container: HTMLElement) => unknown)|null} [onNotificationDisplayedCallback] Callback calls after prepending
     * @param {boolean} autoClose Notification should auto-close after 10 seconds
     * @returns {boolean | unknown} Value returned by optional callback or boolean true
     */
    static success(message, container, onNotificationDisplayedCallback, autoClose = false) {
        return show('success', message, container, onNotificationDisplayedCallback, autoClose);
    }

    /**
     * Show info alert box with the specified type and message.
     * The alert box will be prepended to the specified container.
     *
     * @param {!(string|MessageWithTitle)} message Text to display
     * @param {HTMLElement} [container] If not provided - use document.body
     * @param {((container: HTMLElement) => unknown)|null} [onNotificationDisplayedCallback] Callback calls after prepending
     * @param {boolean} autoClose Notification should auto-close after 10 seconds
     * @returns {boolean | unknown} Value returned by optional callback or boolean true
     */
    static info(message, container, onNotificationDisplayedCallback, autoClose = false) {
        return show('info', message, container, onNotificationDisplayedCallback, autoClose);
    }

    /**
     * Show warning alert box with the specified type and message.
     * The alert box will be prepended to the specified container.
     *
     * @param {!(string|MessageWithTitle)} message Text to display
     * @param {HTMLElement} [container] If not provided - use document.body
     * @param {((container: HTMLElement) => unknown)|null} [onNotificationDisplayedCallback] Callback calls after prepending
     * @param {boolean} autoClose Notification should auto-close after 10 seconds
     * @returns {boolean | unknown} Value returned by optional callback or boolean true
     */
    static warning(message, container, onNotificationDisplayedCallback, autoClose = false) {
        return show('warning', message, container, onNotificationDisplayedCallback, autoClose);
    }

    /**
     * Show alert box with the specified type and message.
     * The alert box will be prepended to the specified container.
     *
     * @param {!(string|MessageWithTitle)} message Text to display
     * @param {HTMLElement} [container] If not provided - use document.body
     * @param {((container: HTMLElement) => unknown)|null} [onNotificationDisplayedCallback] Callback calls after prepending
     * @param {boolean} autoClose Notification should auto-close after 10 seconds
     * @returns {boolean | unknown} Value returned by optional callback or boolean true
     */
    static error(message, container, onNotificationDisplayedCallback, autoClose = false) {
        return show('error', message, container, onNotificationDisplayedCallback, autoClose);
    }
}
